import type { ChargingSessionState } from "../../config/chargingSession";
import { NightAiScenarioIcon } from "./NightAiScenarioIcon";
import { NightLargeGlowCardSurface } from "./NightLargeGlowCardSurface";
import "./NightCards.css";

interface NightChargingSessionCardProps {
  session: ChargingSessionState;
  onConfirmCover: () => void;
  onDismiss: () => void;
  onEnableRestMode: () => void;
  onShowRestRecommendation: () => void;
}

export function NightChargingSessionCard({
  session,
  onConfirmCover,
  onDismiss,
  onEnableRestMode,
  onShowRestRecommendation
}: NightChargingSessionCardProps) {
  if (session.stage === "idle") {
    return null;
  }

  const copy = getChargingSessionCopy(session);
  const showChargingStatus = session.stage !== "coverPrompt";
  const showVideoRecommendation = session.stage === "restRecommend" || session.stage === "restActive";

  return (
    <section
      className={`night-charging-session-card night-charging-session-card--${session.stage}`}
      aria-label={copy.title}
    >
      {showVideoRecommendation ? (
        <NightLargeGlowCardSurface />
      ) : (
        <span className="night-charging-session-card__surface" aria-hidden="true" />
      )}
      <span className="night-ai-scenario-icon night-ai-scenario-icon--charge night-charging-session-card__icon">
        <NightAiScenarioIcon icon="charge" />
      </span>

      <div className="night-charging-session-card__main">
        <h2>{copy.title}</h2>
        <p>{copy.description}</p>
      </div>

      <div className="night-charging-session-card__facts" aria-label="充电状态">
        <span>
          <strong>{session.currentBatteryLevel}%</strong>
          <small>当前电量</small>
        </span>
        <span>
          <strong>{session.targetBatteryLevel}%</strong>
          <small>目标电量</small>
        </span>
        <span>
          <strong>{session.estimatedChargeMinutes}分钟</strong>
          <small>预计补能</small>
        </span>
      </div>

      {showChargingStatus ? (
        <div className="night-charging-session-card__status" aria-label="车辆正在充电">
          <span className="night-charging-session-card__status-dot" aria-hidden="true" />
          <span>
            <strong>车辆正在充电</strong>
            <small>当前 {session.currentBatteryLevel}% · 预计 {session.estimatedFinishLabel} 补至 {session.targetBatteryLevel}%</small>
          </span>
        </div>
      ) : null}

      {showVideoRecommendation ? (
        <div className="night-charging-session-card__video" aria-label="视频推荐">
          <span>{session.video.category}</span>
          <strong>{session.video.title}</strong>
          <small>{session.video.durationMinutes}分钟 · {session.video.reason}</small>
        </div>
      ) : null}

      <div className="night-charging-session-card__actions">
        <button
          type="button"
          className="night-charging-session-card__action night-charging-session-card__action--primary"
          onClick={() => {
            if (copy.primaryAction === "confirmCover") {
              onConfirmCover();
              return;
            }

            if (copy.primaryAction === "showRest") {
              onShowRestRecommendation();
              return;
            }

            onEnableRestMode();
          }}
        >
          {copy.primaryLabel}
        </button>
        <button
          type="button"
          className="night-charging-session-card__action night-charging-session-card__action--secondary"
          onClick={onDismiss}
        >
          {copy.secondaryLabel}
        </button>
      </div>
    </section>
  );
}

function getChargingSessionCopy(session: ChargingSessionState) {
  if (session.stage === "coverPrompt") {
    return {
      description: `已到达 ${session.stationName}，检测到您已泊入充电位，是否为您自动打开充电盖？`,
      primaryAction: "confirmCover" as const,
      primaryLabel: "确认打开",
      secondaryLabel: "稍后",
      title: "已泊入充电位"
    };
  }

  if (session.stage === "chargingStarted") {
    return {
      description: "充电盖已自动打开，车辆正在建立充电连接。",
      primaryAction: "showRest" as const,
      primaryLabel: "查看休息推荐",
      secondaryLabel: "收起",
      title: "充电盖已打开"
    };
  }

  if (session.stage === "restActive") {
    return {
      description: `休息模式已开启，座舱已调暗屏幕并准备播放《${session.video.title}》。`,
      primaryAction: "enableRest" as const,
      primaryLabel: "继续播放",
      secondaryLabel: "收起",
      title: "休息模式已开启"
    };
  }

  return {
    description: `车辆预计还需补能 ${session.estimatedChargeMinutes} 分钟，已按充电时长推荐休息模式和视频。`,
    primaryAction: "enableRest" as const,
    primaryLabel: "开启休息模式",
    secondaryLabel: "稍后",
    title: "建议开启休息模式"
  };
}
